import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  IconButton,
  Alert,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Add, Delete, Save, Email } from "@mui/icons-material";
import { useSnack } from "../../services/SnakbarProvider";
import { getNotificationConfig, saveNotificationConfig, sendTestNotification } from "../../api/imports/notifications";

export default function ShipmentNotifications({ companyId }) {
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const { openSnack } = useSnack();

  // Default message template
  const defaultMessage = `Se ha creado un nuevo embarque sin orden de compra asociada.

Detalles del embarque:
- Referencia: {shipmentRef}
- Proveedor: {providerName}
- Fecha de solicitud: {requestDate}
- Fecha de entrega: {deliveryDate}

Por favor, revise este embarque y asócielo con la orden de compra correspondiente si es necesario.

Este es un mensaje automático del sistema ERP.`;

  useEffect(() => {
    // Load saved configuration
    loadConfiguration();
  }, [companyId]);

  const loadConfiguration = async () => {
    try {
      const config = await getNotificationConfig(companyId);
      if (config) {
        setEmailRecipients(JSON.parse(config.emailRecipients || '[]'));
        setNotificationMessage(config.notificationMessage || defaultMessage);
        setIsEnabled(config.isEnabled || false);
      } else {
        setNotificationMessage(defaultMessage);
        setIsEnabled(false);
      }
    } catch (error) {
      console.error("Error loading configuration:", error);
      setNotificationMessage(defaultMessage);
      setIsEnabled(false);
    }
  };

  const handleAddEmail = () => {
    if (newEmail && isValidEmail(newEmail)) {
      if (!emailRecipients.includes(newEmail)) {
        setEmailRecipients([...emailRecipients, newEmail]);
        setNewEmail("");
      } else {
        openSnack("Este email ya está en la lista", "warning");
      }
    } else {
      openSnack("Por favor ingrese un email válido", "error");
    }
  };

  const handleRemoveEmail = (emailToRemove) => {
    setEmailRecipients(emailRecipients.filter(email => email !== emailToRemove));
  };

  const handleSaveConfiguration = async () => {
    if (emailRecipients.length === 0) {
      openSnack("Debe agregar al menos un destinatario", "error");
      return;
    }

    if (!notificationMessage.trim()) {
      openSnack("Debe ingresar un mensaje de notificación", "error");
      return;
    }

    setLoading(true);
    try {
      const config = {
        companyId,
        emailRecipients,
        notificationMessage,
        isEnabled,
      };
      
      await saveNotificationConfig(config);
      openSnack("Configuración guardada exitosamente", "success");
    } catch (error) {
      console.error("Error saving configuration:", error);
      openSnack("Error al guardar la configuración", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async () => {
    if (emailRecipients.length === 0) {
      openSnack("Debe agregar al menos un destinatario", "error");
      return;
    }

    setLoading(true);
    try {
      await sendTestNotification(companyId, emailRecipients);
      openSnack("Notificación de prueba enviada", "success");
    } catch (error) {
      console.error("Error sending test notification:", error);
      openSnack("Error al enviar notificación de prueba", "error");
    } finally {
      setLoading(false);
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Configuración de Notificaciones
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configure las notificaciones por email cuando se cree un embarque sin orden de compra asociada.
      </Typography>

      <Grid container spacing={3}>
        {/* Email Recipients Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Destinatarios de Email
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                  placeholder="ejemplo@empresa.com"
                />
                <Button
                  variant="contained"
                  onClick={handleAddEmail}
                  startIcon={<Add />}
                  disabled={!newEmail || !isValidEmail(newEmail)}
                >
                  Agregar
                </Button>
              </Box>

              {emailRecipients.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Destinatarios configurados:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {emailRecipients.map((email, index) => (
                      <Chip
                        key={index}
                        label={email}
                        onDelete={() => handleRemoveEmail(email)}
                        deleteIcon={<Delete />}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {emailRecipients.length === 0 && (
                <Alert severity="info">
                  No hay destinatarios configurados. Agregue al menos uno para recibir notificaciones.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configuración de Notificaciones
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={isEnabled}
                    onChange={(e) => setIsEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label="Habilitar notificaciones automáticas"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Cuando esté habilitado, se enviará un email automáticamente cada vez que se cree un embarque sin orden de compra asociada.
              </Typography>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<Email />}
                  onClick={handleTestNotification}
                  disabled={loading || emailRecipients.length === 0}
                >
                  Enviar Prueba
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Message Template */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Plantilla de Mensaje
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Personalice el mensaje que se enviará en las notificaciones. Puede usar las siguientes variables:
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Variables disponibles: {'{shipmentRef}'}, {'{providerName}'}, {'{requestDate}'}, {'{deliveryDate}'}
                </Typography>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={8}
                value={notificationMessage}
                onChange={(e) => setNotificationMessage(e.target.value)}
                placeholder="Ingrese el mensaje de notificación..."
                variant="outlined"
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => setNotificationMessage(defaultMessage)}
                >
                  Restaurar Predeterminado
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveConfiguration}
                  disabled={loading || emailRecipients.length === 0}
                >
                  {loading ? "Guardando..." : "Guardar Configuración"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
